import { type FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import { Button } from '@/components/buttons';
import { ImageComponent } from '@/components/Image';
import { ThemeMode } from '@/helpers/enums';
import { useToggle } from '@/hooks/common';
import { useABTestingV2 } from '@/modules/abTesting';
import { AB_TESTS } from '@/modules/abTesting/constants';
import { useDepositStore, useGetCommonStore } from '@/store';
import { Spinner } from '@/styles/common';
import { ScCurrencyItem } from './styled';

interface CurrencyItemProps {
  currencyCode: string;
  label: string;
  icon?: string;
  isActive?: boolean;
  disableButton?: boolean;
  noAction?: boolean;
}

export const CurrencyItem: FC<CurrencyItemProps> = ({
  currencyCode,
  label,
  icon,
  isActive,
  disableButton,
  noAction,
}) => {
  const { t } = useTranslation('common');
  const { greenColor, whiteColor, gray1Color } = useTheme();
  const useCommonStore = useGetCommonStore();
  const { isB: isSeparatedWagering } = useABTestingV2({
    experiment: AB_TESTS.SEPARATED_WAGERING,
  });

  const setOriginalAmount = useDepositStore.use.setOriginalAmount();
  const {
    deleteUserWallet,
    changeUserWallet,
    currentWallet,
    pendingWallets,
    isMobileDevice,
    extendedUserWallets,
  } = useCommonStore(
    useShallow((state) => ({
      deleteUserWallet: state.deleteUserWallet,
      changeUserWallet: state.changeUserWallet,
      currentWallet: state.currentWallet,
      pendingWallets: state.pendingWallets,
      isMobileDevice: state.isMobileDevice,
      extendedUserWallets: state.extendedUserWallets,
    }))
  );

  const isCurrentCurrency = useMemo(
    () => currentWallet?.currency === currencyCode,
    [currentWallet?.currency, currencyCode]
  );

  const [pending, togglePending] = useToggle();

  const handleItemClick = useCallback(async () => {
    if (isActive && !isCurrentCurrency) {
      await changeUserWallet(currencyCode);
      setOriginalAmount('');
    }
  }, [
    currencyCode,
    isActive,
    changeUserWallet,
    isCurrentCurrency,
    setOriginalAmount,
  ]);

  const handleActionClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setOriginalAmount('');

      if (isActive) {
        if (currentWallet?.currency === currencyCode) {
          const { currency } = extendedUserWallets.find(
            ({ currency }) => currency !== currencyCode
          )!;
          await deleteUserWallet(currencyCode, currency);
          setOriginalAmount('');
        } else {
          deleteUserWallet(currencyCode);
        }
      } else {
        await changeUserWallet(currencyCode);
        setOriginalAmount('');
      }

      togglePending();
    },
    [
      currencyCode,
      isActive,
      currentWallet,
      extendedUserWallets,
      deleteUserWallet,
      changeUserWallet,
      togglePending,
    ]
  );

  const buttonParams = useMemo(
    () =>
      !isMobileDevice || isCurrentCurrency
        ? { blackTransparent: true }
        : { whiteTransparent: true },
    [isMobileDevice, isCurrentCurrency]
  );

  return (
    <ScCurrencyItem
      isActive={isActive}
      isCurrent={isCurrentCurrency}
      mode={isMobileDevice ? ThemeMode.Dark : ThemeMode.Light}
      noAction={!!noAction}
      onClick={handleItemClick}
    >
      <div className="currency-info">
        {icon && (
          <ImageComponent alt="currency" height={24} src={icon} width={24} />
        )}
        <p className="symbol">{label}</p>
      </div>

      {(isCurrentCurrency || !noAction) && (
        <div className="actions">
          {isCurrentCurrency &&
            (pendingWallets && noAction ? (
              <Spinner
                background={isMobileDevice ? whiteColor : gray1Color}
                color={greenColor}
                margin="0 4px"
                size={20}
              />
            ) : (
              <div
                className={`tick-icon ${isSeparatedWagering ? 'primary' : 'success'}`}
              />
            ))}

          {!noAction && (
            <Button
              {...buttonParams}
              autoPaddingHorizontal
              disabled={disableButton}
              isLoading={pending}
              onClick={handleActionClick}
              paddingX={11}
              xSmall
            >
              {isActive ? t('button.remove') : t('button.add')}
            </Button>
          )}
        </div>
      )}
    </ScCurrencyItem>
  );
};
